import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/toast";
import { Trash2, Plus } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { api } from "@/api/client";
export function SettingsPage() {
    const { activeChannel: channel, user } = useAuthStore();
    const [activeTab, setActiveTab] = useState("channel");
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("h1", { className: "text-2xl font-semibold", children: "Einstellungen" }), _jsx(Tabs, { tabs: [
                    { key: "channel", label: "Kanal" },
                    { key: "editors", label: "Editoren" },
                    { key: "account", label: "Account" },
                ], activeTab: activeTab, onChange: setActiveTab }), activeTab === "channel" && _jsx(ChannelSettingsTab, {}), activeTab === "editors" && channel && _jsx(EditorsTab, { channelId: channel.id }), activeTab === "account" && _jsx(AccountTab, {})] }));
}
function ChannelSettingsTab() {
    const { activeChannel: channel, refreshChannels } = useAuthStore();
    const { show: toast } = useToast();
    const [prefix, setPrefix] = useState(channel?.commandPrefix ?? "!");
    const [state, setState] = useState("idle");
    useEffect(() => {
        if (channel)
            setPrefix(channel.commandPrefix);
    }, [channel]);
    const isDirty = !!channel && prefix !== channel.commandPrefix;
    const handleSave = async () => {
        if (!channel || !isDirty)
            return;
        setState("saving");
        try {
            const res = await api.patch(`/channels/${channel.id}`, { commandPrefix: prefix });
            if (res.error)
                throw new Error(res.error);
            await refreshChannels();
            toast("success", "Command-Prefix gespeichert");
        }
        catch {
            toast("error", "Speichern fehlgeschlagen");
        }
        finally {
            setState("idle");
        }
    };
    const label = state === "saving" ? "Speichere..." : "Speichern";
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: "Channel Settings" }) }), _jsx(CardContent, { className: "space-y-4", children: _jsxs("div", { children: [_jsx(Label, { htmlFor: "command-prefix", children: "Command Prefix" }), _jsxs("div", { className: "flex gap-2 mt-1", children: [_jsx(Input, { id: "command-prefix", value: prefix, onChange: (e) => setPrefix(e.target.value), className: "w-24", maxLength: 3 }), _jsx(Button, { onClick: handleSave, disabled: !isDirty || state === "saving", children: label })] })] }) })] }));
}
function EditorsTab({ channelId }) {
    const [editors, setEditors] = useState([]);
    const [username, setUsername] = useState("");
    const [role, setRole] = useState("editor");
    useEffect(() => {
        loadEditors();
    }, [channelId]);
    const loadEditors = async () => {
        const res = await api.get(`/channels/${channelId}/editors`);
        if (res.data)
            setEditors(res.data);
    };
    const handleInvite = async () => {
        if (!username.trim())
            return;
        await api.post(`/channels/${channelId}/editors`, {
            twitchUsername: username.trim(),
            role,
        });
        setUsername("");
        loadEditors();
    };
    const handleRemove = async (id) => {
        await api.delete(`/channels/${channelId}/editors/${id}`);
        loadEditors();
    };
    const handleRoleChange = async (id, newRole) => {
        await api.patch(`/channels/${channelId}/editors/${id}`, { role: newRole });
        loadEditors();
    };
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: "Channel Editors" }) }), _jsxs(CardContent, { className: "space-y-4", children: [_jsxs("div", { className: "flex gap-2 items-end", children: [_jsxs("div", { className: "flex-1", children: [_jsx(Label, { children: "Twitch Username" }), _jsx(Input, { value: username, onChange: (e) => setUsername(e.target.value), placeholder: "username", onKeyDown: (e) => e.key === "Enter" && handleInvite() })] }), _jsxs("div", { children: [_jsx(Label, { children: "Role" }), _jsxs(Select, { value: role, onChange: (e) => setRole(e.target.value), children: [_jsx("option", { value: "editor", children: "Editor" }), _jsx("option", { value: "viewer", children: "Viewer" })] })] }), _jsxs(Button, { onClick: handleInvite, children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), " Invite"] })] }), _jsxs("div", { className: "space-y-2", children: [editors.map((editor) => (_jsxs("div", { className: "flex items-center justify-between border-b py-2", children: [_jsxs("div", { className: "flex items-center gap-3", children: [editor.avatarUrl && (_jsx("img", { src: editor.avatarUrl, alt: "", className: "h-6 w-6 rounded-full" })), _jsx("span", { className: "font-medium text-sm", children: editor.displayName }), _jsx(Badge, { variant: "secondary", children: editor.role })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Select, { value: editor.role, onChange: (e) => handleRoleChange(editor.id, e.target.value), children: [_jsx("option", { value: "editor", children: "Editor" }), _jsx("option", { value: "viewer", children: "Viewer" })] }), _jsx(Button, { size: "icon", variant: "ghost", "aria-label": `${editor.displayName} aus den Editoren entfernen`, onClick: () => {
                                                    if (confirm(`${editor.displayName} wirklich als ${editor.role} entfernen?`))
                                                        handleRemove(editor.id);
                                                }, children: _jsx(Trash2, { className: "h-4 w-4 text-destructive" }) })] })] }, editor.id))), editors.length === 0 && (_jsx("p", { className: "text-sm text-muted-foreground py-4 text-center", children: "No editors yet. Invite someone to help manage your channel." }))] })] })] }));
}
function AccountTab() {
    const { activeChannel: channel, user } = useAuthStore();
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: "Account" }) }), _jsxs(CardContent, { className: "space-y-2 text-sm", children: [_jsxs("p", { children: [_jsx("span", { className: "text-muted-foreground", children: "Display Name:" }), " ", user?.displayName] }), _jsxs("p", { children: [_jsx("span", { className: "text-muted-foreground", children: "Twitch ID:" }), " ", user?.twitchId] }), _jsxs("p", { children: [_jsx("span", { className: "text-muted-foreground", children: "Channel ID:" }), " ", channel?.id] })] })] }));
}
